import { graphql } from 'react-relay';
import { useCallback, useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type { GqlRestrictionsInput } from '../RestrictionsGql/convertRestrictionsToGqlRestrictionsInput.ts';
import { convertRestrictionsToGqlRestrictionsInput } from '../RestrictionsGql/convertRestrictionsToGqlRestrictionsInput.ts';

import type {
	useFetchRestrictionsRESTObjRestrictionsRESTQuery as useFetchRestrictionsRESTObjRestrictionsRESTQuery$data,
	useFetchRestrictionsRESTObjRestrictionsRESTQueryVariables as useFetchRestrictionsRESTObjRestrictionsRESTQuery$variables,
} from './__types__/useFetchRestrictionsRESTObjRestrictionsRESTQuery.ts';
import { transformGraphQLQueryToRESTObj } from './restrictionsRESTUtils.ts';
import type { RestrictionsRESTObj } from './restrictionsRESTUtils.ts';
const useFetchRestrictionsRESTObjRestrictionsRESTQuery = graphql`
	query useFetchRestrictionsRESTObjRestrictionsRESTQuery($contentId: ID!, $draftShareId: String) {
		content(id: $contentId, status: ["current", "draft"], draftShareId: $draftShareId) {
			nodes {
				id
				type
				restrictions {
					read {
						operation
						restrictions {
							groupWithRestrictions {
								nodes {
									id
									name
									usageType
								}
							}
							userWithRestrictions {
								nodes {
									accountId
									displayName
									email
									hasSpaceEditPermission
									hasSpaceViewPermission
									permissionType
									publicName
									profilePicture {
										path
									}
									type
								}
							}
						}
					}
					update {
						operation
						restrictions {
							groupWithRestrictions {
								nodes {
									id
									name
									usageType
								}
							}
							userWithRestrictions {
								nodes {
									accountId
									displayName
									email
									hasSpaceEditPermission
									hasSpaceViewPermission
									permissionType
									publicName
									profilePicture {
										path
									}
									type
								}
							}
						}
					}
				}
			}
		}
	}
`;
export type UseFetchRestrictionsRESTObjParamsType = {
	contentId: string;
	onSuccess?: (restrictions: RestrictionsRESTObj) => void;
	/**
	 * Only called when 'use_graphql_to_publish_pages_and_blogs' experiment is enabled.
	 * Will replace onSuccess when experiment is cleaned up
	 */
	onSuccessGql?: (restrictions: GqlRestrictionsInput) => void;
};
export type UseFetchRestrictionsRESTObjType = {
	error: Error | ApolloErrorInterop | undefined;
	refetch: () => Promise<void>;
	/** True while the restrictions query is in flight (initial fetch and refetch). */
	loading: boolean;
};
export const useFetchRestrictionsRESTObj = ({
	contentId,
	onSuccess,
	onSuccessGql,
}: UseFetchRestrictionsRESTObjParamsType): UseFetchRestrictionsRESTObjType => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [transformationError, setTransformationError] = useState<Error | undefined>(undefined);

	const applyRestrictions = useCallback(
		(completedData: useFetchRestrictionsRESTObjRestrictionsRESTQuery$data) => {
			try {
				onSuccessGql?.(convertRestrictionsToGqlRestrictionsInput(completedData));
				onSuccess?.(transformGraphQLQueryToRESTObj(completedData));
				setTransformationError(undefined);
			} catch (restrictionsError) {
				setTransformationError(restrictionsError);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'error',
						actionSubject: 'fetchRestrictions',
						actionSubjectId: 'fetchRestrictionsError',
						source: 'useFetchRestrictionsRESTObj',
						attributes: {
							message: restrictionsError.message,
						},
					},
				}).fire();
			}
		},
		[onSuccess, onSuccessGql, createAnalyticsEvent],
	);

	const { data, error, refetch, loading } = useQueryInterop<
		useFetchRestrictionsRESTObjRestrictionsRESTQuery$data,
		useFetchRestrictionsRESTObjRestrictionsRESTQuery$variables
	>(useFetchRestrictionsRESTObjRestrictionsRESTQuery, {
		variables: {
			contentId,
			draftShareId: getDraftShareIdFromQueryParam(),
		},
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'network-only',
		// confluence_publish_modal_restrictions_refetch is nested inside the fix below so the two can
		// roll out independently — see the refetch wrappers further down for the treatment arm.
		onCompleted: UNSAFE_expValEqualsNoExposure(
			'confluence_publish_modal_restricted_loading_fix',
			'isEnabled',
			true,
		)
			? UNSAFE_expValEqualsNoExposure(
					'confluence_publish_modal_restrictions_refetch',
					'isEnabled',
					true,
				)
				? applyRestrictions
				: undefined
			: applyRestrictions,
	});

	// Control arm for confluence_publish_modal_restrictions_refetch: reproduces today's production
	// behaviour (the useEffect-on-data delivery), since onCompleted does not fire for refetches.
	useEffect(() => {
		if (!data) {
			return;
		}
		if (
			UNSAFE_expValEqualsNoExposure(
				'confluence_publish_modal_restricted_loading_fix',
				'isEnabled',
				true,
			)
		) {
			if (
				!UNSAFE_expValEqualsNoExposure(
					'confluence_publish_modal_restrictions_refetch',
					'isEnabled',
					true,
				)
			) {
				applyRestrictions(data);
			}
		}
	}, [data, applyRestrictions]);

	/** Applies the refetch result directly, since `onCompleted` does not fire for refetches. */
	const refetchAndApply = useCallback(async () => {
		const result = await refetch().catch(() => undefined);
		if (result?.data) {
			applyRestrictions(result.data);
		}
	}, [refetch, applyRestrictions]);

	/** Refetches without re-applying the result. */
	const refetchOnly = useCallback(async () => {
		await refetch().catch(() => undefined);
	}, [refetch]);

	return {
		error: error || transformationError,
		refetch: UNSAFE_expValEqualsNoExposure(
			'confluence_publish_modal_restricted_loading_fix',
			'isEnabled',
			true,
		)
			? UNSAFE_expValEqualsNoExposure(
					'confluence_publish_modal_restrictions_refetch',
					'isEnabled',
					true,
				)
				? refetchAndApply
				: refetchOnly
			: refetchOnly,
		loading,
	};
};
